import { useCallback } from "react";
import useSWR from "swr";
import { PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { createAdminCrudApi } from "../createAdminCrudApi";
import { CustomError } from "../errors";
import type { ExampleAssembly, SearchExampleAssembliesResponse } from "./types/exampleAssembly";
import type { Material, SearchMaterialsResponse } from "./types/material";
import type { CondCategoryBody, SearchCondCategoriesResponse } from "./types/condCategory";
import type { MaterialCategoryBody, SearchMaterialCategoriesResponse } from "./types/materialCategory";
import type { MaterialSubcategoryBody, SearchMaterialSubcategoriesResponse } from "./types/materialSubcategory";

const moduleUrl = "/admin/modules/envelope_designer";
const materialsUrl = `${moduleUrl}/materials`;
const examplesUrl = `${moduleUrl}/example_assemblies`;
// the whole classification lists (a few dozen rows: one page of the maximum size)
const subcategoriesUrl = `${moduleUrl}/material_subcategories?limit=400`;
const categoriesUrl = `${moduleUrl}/material_categories?limit=400`;
const condCategoriesUrl = `${moduleUrl}/cond_categories?limit=400`;

export type MaterialBody = Omit<Material, "id" | "code" | "visibility" | "order">;
export type ExampleAssemblyBody = Omit<ExampleAssembly, "id" | "visibility" | "order">;

// Admin CRUD of the material library's classification: categories and subcategories (keyed by code), and the
// admissible-condensate categories (edit only).
export const useMaterialClassificationApi = () => {
  const api = useApi();
  return {
    categories: createAdminCrudApi<SearchMaterialCategoriesResponse, MaterialCategoryBody, MaterialCategoryBody>(
      api,
      moduleUrl,
      "material_categories",
    ),
    subcategories: createAdminCrudApi<
      SearchMaterialSubcategoriesResponse,
      MaterialSubcategoryBody,
      MaterialSubcategoryBody
    >(api, moduleUrl, "material_subcategories"),
    condCategories: createAdminCrudApi<SearchCondCategoriesResponse, never, CondCategoryBody>(
      api,
      moduleUrl,
      "cond_categories",
    ),
  };
};

// Admin CRUD of the envelope designer catalogs (internal/controllers/admin/envelope_designer).
export const useAdminEnvelopeDesignerApi = () => {
  const api = useApi();

  const useAdminMaterials = useCallback(
    (query: PaginationQuery) =>
      useSWR<SearchMaterialsResponse, CustomError, string>(buildUrl(materialsUrl, query), (url) =>
        api.get<SearchMaterialsResponse>({ url }),
      ),
    [api],
  );

  // Every material (any visibility), for the example layers' material pickers: the backend caps a
  // page at 400, so pages are fetched until `total`.
  const useAllAdminMaterials = useCallback(
    () =>
      useSWR<Material[], CustomError, string>(`${materialsUrl}?all`, async () => {
        const items: Material[] = [];
        for (let page = 1; ; page++) {
          const response = await api.get<SearchMaterialsResponse>({ url: `${materialsUrl}?limit=400&page=${page}` });
          items.push(...response.items);
          if (response.items.length === 0 || items.length >= response.total) break;
        }
        return items.sort((first, second) => first.name.localeCompare(second.name));
      }),
    [api],
  );

  // Every subcategory of the library (a few dozen), for the material and the classification forms.
  const useAdminMaterialSubcategories = useCallback(
    () =>
      useSWR<SearchMaterialSubcategoriesResponse, CustomError, string>(subcategoriesUrl, (url) =>
        api.get<SearchMaterialSubcategoriesResponse>({ url }),
      ),
    [api],
  );

  // The categories and the admissible-condensate categories of the library (a handful), for the material and the
  // classification forms.
  const useAdminMaterialCategories = useCallback(
    () =>
      useSWR<SearchMaterialCategoriesResponse, CustomError, string>(categoriesUrl, (url) =>
        api.get<SearchMaterialCategoriesResponse>({ url }),
      ),
    [api],
  );
  const useAdminCondCategories = useCallback(
    () =>
      useSWR<SearchCondCategoriesResponse, CustomError, string>(condCategoriesUrl, (url) =>
        api.get<SearchCondCategoriesResponse>({ url }),
      ),
    [api],
  );

  const useAdminExampleAssemblies = useCallback(
    () =>
      useSWR<SearchExampleAssembliesResponse, CustomError, string>(examplesUrl, (url) =>
        api.get<SearchExampleAssembliesResponse>({ url }),
      ),
    [api],
  );

  // Writes return nothing useful: the lists are revalidated instead.
  const write = useCallback(async (baseUrl: string, request: () => Promise<unknown>) => {
    await request();
    mutateCache([baseUrl]);
  }, []);

  return {
    useAdminMaterials,
    useAllAdminMaterials,
    useAdminMaterialSubcategories,
    useAdminMaterialCategories,
    useAdminCondCategories,
    useAdminExampleAssemblies,
    saveMaterial: (id: string | null, body: MaterialBody) =>
      write(materialsUrl, () => (id ? api.put(`${materialsUrl}/${id}`, body) : api.post(materialsUrl, body))),
    deleteMaterial: (id: string) => write(materialsUrl, () => api.del(`${materialsUrl}/${id}`)),
    updateMaterialVisibility: (id: string, visibility: string) =>
      write(materialsUrl, () => api.put(`${materialsUrl}/${id}/visibility`, { visibility })),
    updateMaterialOrder: (id: string, order: number) =>
      write(materialsUrl, () => api.put(`${materialsUrl}/${id}/order`, { order })),
    saveExampleAssembly: (id: string | null, body: ExampleAssemblyBody) =>
      write(examplesUrl, () => (id ? api.put(`${examplesUrl}/${id}`, body) : api.post(examplesUrl, body))),
    deleteExampleAssembly: (id: string) => write(examplesUrl, () => api.del(`${examplesUrl}/${id}`)),
    updateExampleAssemblyVisibility: (id: string, visibility: string) =>
      write(examplesUrl, () => api.put(`${examplesUrl}/${id}/visibility`, { visibility })),
    updateExampleAssemblyOrder: (id: string, order: number) =>
      write(examplesUrl, () => api.put(`${examplesUrl}/${id}/order`, { order })),
  };
};
